import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { subscriptionsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, MessageSquare, Loader2, Copy, Mail, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';

const Negotiate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [currentPrice, setCurrentPrice] = useState('');
  const [desiredPrice, setDesiredPrice] = useState('');
  const [reason, setReason] = useState('');
  const [emailContent, setEmailContent] = useState(null);
  const [potentialSavings, setPotentialSavings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, [id]);

  const loadSubscription = async () => {
    try {
      const response = await subscriptionsApi.getOne(id);
      setSubscription(response.data);
      setCurrentPrice(response.data.amount.toString());
    } catch (error) {
      toast.error('Failed to load subscription');
      navigate('/subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateEmail = async () => {
    if (!currentPrice || !desiredPrice) {
      toast.error('Please enter both current and desired prices');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await subscriptionsApi.generateNegotiation(
        id,
        parseFloat(currentPrice),
        parseFloat(desiredPrice),
        reason
      );
      setEmailContent(response.data.email_content);
      setPotentialSavings(response.data.potential_savings);
      toast.success('Negotiation email generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate email');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="negotiate-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              Negotiate Price
            </h1>
            <p className="text-gray-500">Generate a persuasive email to lower your bill</p>
          </div>
        </div>

        {/* Subscription Info */}
        <Card className="border border-gray-100 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#D1FAE5] rounded-2xl flex items-center justify-center text-[#064E3B] text-xl font-bold">
                {subscription?.name?.charAt(0)}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{subscription?.name}</h2>
                <p className="text-gray-500">
                  Currently paying ${subscription?.amount}/{subscription?.billing_cycle}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Potential Savings Banner */}
        {potentialSavings > 0 && (
          <Card className="border-0 bg-gradient-to-r from-[#D1FAE5] to-[#A7F3D0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/50 rounded-xl flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-[#064E3B]" />
                </div>
                <div>
                  <p className="font-medium text-[#064E3B]">Potential Annual Savings</p>
                  <p className="text-2xl font-bold text-[#064E3B]">${potentialSavings}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        {!emailContent && (
          <Card className="border border-gray-100 shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>
                Negotiation Details
              </CardTitle>
              <CardDescription>
                Tell us what you're paying and what you'd like to pay
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPrice">Current Price *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      id="currentPrice"
                      type="number"
                      step="0.01"
                      placeholder="9.99"
                      value={currentPrice}
                      onChange={(e) => setCurrentPrice(e.target.value)}
                      className="pl-8 bg-gray-50 border-gray-200 focus:bg-white"
                      data-testid="current-price-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="desiredPrice">Desired Price *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      id="desiredPrice"
                      type="number"
                      step="0.01"
                      placeholder="6.99"
                      value={desiredPrice}
                      onChange={(e) => setDesiredPrice(e.target.value)}
                      className="pl-8 bg-gray-50 border-gray-200 focus:bg-white"
                      data-testid="desired-price-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Your Leverage (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="e.g., I've been a loyal customer for 3 years, I found a competitor offering $5.99/month, my budget is tight..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="bg-gray-50 border-gray-200 focus:bg-white min-h-24"
                  data-testid="reason-input"
                />
                <p className="text-xs text-gray-400">
                  Adding leverage helps generate a more persuasive email
                </p>
              </div>

              <Button
                onClick={handleGenerateEmail}
                disabled={isGenerating}
                className="w-full bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Email...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Generate Negotiation Email
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Generated Email */}
        {emailContent && (
          <Card className="border border-gray-100 shadow-soft">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Mail className="w-5 h-5 text-[#064E3B]" />
                    Your Negotiation Email
                  </CardTitle>
                  <CardDescription>
                    Copy and send this email to {subscription?.name}'s support
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(emailContent)}
                  className="rounded-lg"
                  data-testid="copy-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <pre className="whitespace-pre-wrap text-gray-700 font-sans text-sm leading-relaxed" data-testid="email-content">
                  {emailContent}
                </pre>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEmailContent(null);
                    setPotentialSavings(0);
                  }}
                  className="flex-1 rounded-xl"
                  data-testid="regenerate-btn"
                >
                  Generate New Email
                </Button>
                <Button
                  onClick={() => copyToClipboard(emailContent)}
                  className="flex-1 bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
                  data-testid="copy-send-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy & Send
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Negotiate;
